/**
 * Ein nummerierter Schritt im geführten Ablauf — der rote Faden.
 *
 * 🔴 Regel (Matze, 15.09.2026): „Ich möchte entweder erweitern/updaten oder neu anlegen
 * und nicht so verwurstelte Übersichten. Eine klare, strukturierte Guideline."
 *
 * Deshalb bekommt jeder Block eine Nummer, einen Titel, eine Zeile Zustand („90 Artikel
 * ausgewählt") und sonst nichts. Was gerade nicht dran ist, ist zugeklappt, aber
 * sichtbar — der Nutzer sieht immer, wo er steht und was noch kommt.
 *
 * `zustand` ist die eine Zeile, die den Schritt zusammenfasst, wenn er zugeklappt ist.
 * `erledigt` malt das Häkchen. Beides kommt vom Aufrufer, die Komponente rät nichts.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { Check, ChevronDown, ChevronRight } from 'lucide-react';

export interface SchrittProps {
  nummer: number;
  titel: string;
  /** Eine Zeile Klartext zum Stand — sichtbar, auch wenn zugeklappt. */
  zustand?: ReactNode;
  erledigt?: boolean;
  /** Warnfarbe für Schritte, die wirklich schreiben. */
  ton?: 'normal' | 'warnung' | 'gefahr';
  /**
   * Zu Beginn offen? Ohne Angabe: offen, solange der Schritt nicht `erledigt` ist.
   *
   * 🔑 So ist immer nur der Schritt offen, der gerade dran ist — die übrigen bleiben
   * als Zeile sichtbar. Entschieden wird das beim ersten Aufbau: ein Schritt, der
   * während der Arbeit fertig wird, klappt NICHT unter den Händen weg.
   */
  offen?: boolean;
  /**
   * Details, die nur selten jemand braucht — Begründungen, Messwerte, Sonderfälle.
   * Sie liegen hinter „Mehr dazu" statt im Fließtext.
   *
   * 🔴 Regel (Matze, 25.09.2026): „Das ist mir alles zu viel Text und Info. Die Infos
   * sollen lieber eine Ebene tiefer liegen — der Anwender wird sonst erschlagen."
   */
  hilfe?: ReactNode;
  /** Ein Schritt, der noch nicht dran ist — ausgegraut, nicht aufklappbar. */
  gesperrt?: boolean;
  gesperrtGrund?: string;
  /** Wird aufgerufen, sobald der Schritt offen ist (z. B. Probelauf von selbst starten). */
  aufOeffnen?: () => void;
  children: ReactNode;
}

export default function Schritt({
  nummer, titel, zustand, erledigt = false, ton = 'normal',
  offen: offenStart, gesperrt = false, gesperrtGrund, hilfe, aufOeffnen, children,
}: SchrittProps) {
  const [offen, setOffen] = useState(offenStart ?? !erledigt);
  const [hilfeOffen, setHilfeOffen] = useState(false);
  const istOffen = offen && !gesperrt;
  useEffect(() => {
    if (istOffen) aufOeffnen?.();
    // nur auf das Aufklappen reagieren, nicht auf jede neue Funktions-Identität
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [istOffen]);

  const rahmen = gesperrt
    ? 'border-[#DAD9D9] opacity-60'
    : ton === 'gefahr'
      ? 'border-red-300'
      : ton === 'warnung'
        ? 'border-amber-300'
        : 'border-[#DAD9D9]';

  return (
    <section className={`rounded border-2 ${rahmen} bg-white`}>
      <button
        type="button"
        onClick={() => { if (!gesperrt) setOffen(v => !v); }}
        disabled={gesperrt}
        className="flex w-full items-start gap-3 px-4 py-3 text-left disabled:cursor-not-allowed"
        aria-expanded={istOffen}
      >
        <span
          className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-sm font-bold ${
            erledigt
              ? 'bg-[#1A1A1A] text-[#E9FF57]'
              : gesperrt
                ? 'bg-[#DAD9D9] text-[#1A1A1A]/50'
                : 'bg-[#E9FF57] text-[#1A1A1A]'
          }`}
          aria-hidden
        >
          {erledigt ? <Check className="h-4 w-4" /> : nummer}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block font-semibold text-[#1A1A1A]">{titel}</span>
          {gesperrt && gesperrtGrund
            ? <span className="block text-sm text-[#1A1A1A]/60">{gesperrtGrund}</span>
            : zustand
              ? <span className="block text-sm text-[#1A1A1A]/70">{zustand}</span>
              : null}
        </span>
        {!gesperrt && (
          istOffen
            ? <ChevronDown className="mt-1 h-4 w-4 shrink-0 text-[#1A1A1A]/50" aria-hidden />
            : <ChevronRight className="mt-1 h-4 w-4 shrink-0 text-[#1A1A1A]/50" aria-hidden />
        )}
      </button>

      {istOffen && (
        <div className="border-t border-[#DAD9D9] px-4 py-4">
          {children}

          {hilfe && (
            <div className="mt-4 border-t border-[#DAD9D9] pt-3">
              <button
                type="button"
                onClick={() => setHilfeOffen(v => !v)}
                className="text-xs text-[#1A1A1A]/50 underline-offset-2 hover:underline"
              >
                {hilfeOffen ? 'Weniger' : 'Mehr dazu'}
              </button>
              {hilfeOffen && (
                <div className="mt-2 space-y-1 text-xs text-[#1A1A1A]/60">{hilfe}</div>
              )}
            </div>
          )}
        </div>
      )}
    </section>
  );
}
